import { useEffect, useRef, useState } from 'react';

import lottie, { AnimationItem } from 'lottie-web';
import { toast } from 'sonner';

import CompanySelectionModal from './CompanySelectionModal';
import {
  BrandingResult,
  CompanyEnrichmentResponse,
  OnboardingPositionChoice,
  OnboardingProfileData,
  enrichCompany,
  enrichProfile,
  mergeCompanySelection,
  toBrandingResult,
  toOnboardingProfileData,
} from './onboardingApi';
import animationData from './profileBuilderAnimation.json';

// ---------------------------------------------------------------------------
// Phase definitions — each phase corresponds to a form section
// ---------------------------------------------------------------------------

const PHASES = [
  {
    label: 'Finding profile information…',
    done: 'Profile information found',
    section: 'Personal information',
  },
  {
    label: 'Finding company details…',
    done: 'Company details found',
    section: 'Company & social links',
  },
  {
    label: 'Building your profile…',
    done: 'Profile ready',
    section: 'Profile settings',
  },
] as const;

const PHASE_DELAYS_MS = [0, 1400, 2800];

// ---------------------------------------------------------------------------
// Skeleton primitives
// ---------------------------------------------------------------------------

function SkeletonLine({ w = 'w-full', h = 'h-4' }: { w?: string; h?: string }) {
  return <div className={`${w} ${h} rounded bg-gray-200 animate-pulse`} />;
}

function SkeletonSection({
  title,
  phaseIndex,
  currentPhase,
  children,
}: {
  title: string;
  phaseIndex: number;
  currentPhase: number;
  children: React.ReactNode;
}) {
  const isDone = currentPhase > phaseIndex;
  const isActive = currentPhase === phaseIndex;

  return (
    <div
      className={`rounded-xl border bg-white px-6 py-6 transition-all duration-500 ${
        isDone
          ? 'border-green-200 opacity-50'
          : isActive
          ? 'border-brand-300 shadow-md'
          : 'border-gray-200 opacity-40'
      }`}
    >
      {/* Section header */}
      <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100">
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        {isDone && (
          <span className="text-xs text-green-600 font-medium flex items-center gap-1">
            <span className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-[10px]">
              ✓
            </span>
            {PHASES[phaseIndex].done}
          </span>
        )}
        {isActive && (
          <span className="text-xs text-brand-600 font-medium animate-pulse">
            {PHASES[phaseIndex].label}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function runCompanyLookup(
  position: OnboardingPositionChoice,
  previewToken: string,
): Promise<CompanyEnrichmentResponse | null> {
  if (!position.companyLinkedInUrl) return null;
  try {
    return await enrichCompany(position.companyLinkedInUrl, previewToken);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  /** The LinkedIn URL entered by the user in step 1. */
  linkedInUrl: string;
  onComplete: (
    scraped: OnboardingProfileData | null,
    branding: BrandingResult | null,
    previewToken: string | null,
  ) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function LottieTransition({ linkedInUrl, onComplete }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);
  const [phase, setPhase] = useState(0);

  const animDoneRef = useRef(false);
  const apisDoneRef = useRef(false);
  const brandResultRef = useRef<BrandingResult | null>(null);
  const scrapedDataRef = useRef<OnboardingProfileData | null>(null);
  const previewTokenRef = useRef<string | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const completedRef = useRef(false);
  const [fadingOut, setFadingOut] = useState(false);

  // Company selection modal — held until the animation reaches the "company
  // details" phase (phase >= 1, ~1.4 s) so the pop-up feels contextually timed.
  const [pendingModalData, setPendingModalData] = useState<{
    profileData: OnboardingProfileData;
    positions: OnboardingPositionChoice[];
  } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [companyLookupLoading, setCompanyLookupLoading] = useState(false);

  // Show the company modal as soon as the animation hits the "company details"
  // phase AND the enrichment API has already returned multiple positions.
  useEffect(() => {
    if (phase >= 1 && pendingModalData !== null && !showModal) {
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, pendingModalData]);

  // Called whenever both the animation and all API work are complete
  const tryFinish = () => {
    if (completedRef.current) return;
    if (animDoneRef.current && apisDoneRef.current) {
      completedRef.current = true;
      setFadingOut(true);
      setTimeout(() => {
        onCompleteRef.current(
          scrapedDataRef.current,
          brandResultRef.current,
          previewTokenRef.current,
        );
      }, 450);
    }
  };

  // Status text cycling
  useEffect(() => {
    const timers = PHASE_DELAYS_MS.slice(1).map((delay, i) =>
      setTimeout(() => setPhase(i + 1), delay),
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  // Lottie animation — no guard so cleanup + remount cycle works in StrictMode
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    animDoneRef.current = false;

    const anim = lottie.loadAnimation({
      container,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      animationData,
    });
    animRef.current = anim;

    const handleComplete = () => {
      animDoneRef.current = true;
      tryFinish();
    };
    anim.addEventListener('complete', handleComplete);

    return () => {
      anim.removeEventListener('complete', handleComplete);
      anim.destroy();
      animRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // All API work — runs immediately on mount in parallel with the animation
  // ---------------------------------------------------------------------------

  const apiStartedRef = useRef(false);

  useEffect(() => {
    if (apiStartedRef.current) return;
    apiStartedRef.current = true;
    runAllApis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runAllApis() {
    // ── Step 1: Profile enrichment ──────────────────────────────────────────
    try {
      const response = await enrichProfile(linkedInUrl);
      previewTokenRef.current = response.preview_token;

      const profileData = toOnboardingProfileData(response);
      scrapedDataRef.current = profileData;

      if (response.status === 'pending_company_selection') {
        setPendingModalData({
          profileData,
          positions: response.positions,
        });
        return;
      }

      brandResultRef.current = await toBrandingResult(response.branding);
      apisDoneRef.current = true;
      tryFinish();
    } catch {
      toast.error(
        'Could not load LinkedIn profile — you can fill in your details manually.',
      );
      apisDoneRef.current = true;
      tryFinish();
      return;
    }
  }

  async function continueWithPosition(
    data: OnboardingProfileData,
    position: OnboardingPositionChoice,
  ) {
    const previewToken = previewTokenRef.current;

    if (!previewToken) {
      apisDoneRef.current = true;
      tryFinish();
      return;
    }

    const fetchedCompany = await runCompanyLookup(position, previewToken);
    if (!fetchedCompany) {
      scrapedDataRef.current = data;
      brandResultRef.current = null;
      apisDoneRef.current = true;
      tryFinish();
      return;
    }

    const updated = mergeCompanySelection(data, position, fetchedCompany);
    scrapedDataRef.current = updated;
    brandResultRef.current = await toBrandingResult(fetchedCompany.branding);
    apisDoneRef.current = true;
    tryFinish();
  }

  // Called when the user picks a company from the modal
  const handleCompanySelect = async (position: OnboardingPositionChoice) => {
    if (!pendingModalData) return;
    setCompanyLookupLoading(true);
    try {
      await continueWithPosition(pendingModalData.profileData, position);
      setShowModal(false);
    } finally {
      setCompanyLookupLoading(false);
    }
  };

  // Called when the user dismisses the modal without selecting a company.
  // Treat as "skip" — continue without company enrichment so the transition
  // can complete instead of hanging indefinitely.
  const handleModalClose = () => {
    if (!pendingModalData) return;
    scrapedDataRef.current = pendingModalData.profileData;
    brandResultRef.current = null;
    setPendingModalData(null);
    setShowModal(false);
    apisDoneRef.current = true;
    tryFinish();
  };

  // ---------------------------------------------------------------------------
  // Render — mirrors the Layout + step-2 structure exactly
  // ---------------------------------------------------------------------------

  return (
    <div
      className="h-[100dvh] flex overflow-hidden bg-white"
      style={{ opacity: fadingOut ? 0 : 1, transition: 'opacity 450ms' }}
    >
      {/* ── Sidebar skeleton (desktop only) ────────────────────────────── */}
      <div className="hidden lg:flex flex-shrink-0 border-r border-gray-200">
        <div className="flex flex-col w-[235px] h-[100dvh] bg-white">
          {/* Logo row */}
          <div className="flex items-center h-20 px-4 border-b border-transparent">
            <div className="w-20 h-7 rounded bg-gray-200 animate-pulse" />
          </div>
          {/* Nav skeletons */}
          <div className="flex-1 p-3 space-y-6 overflow-hidden">
            {(['Organisation', 'Insights', 'Accessories'] as const).map(
              label => (
                <div key={label} className="space-y-2">
                  <div className="w-20 h-3 rounded bg-gray-200 animate-pulse" />
                  {[1, 2].map(i => (
                    <div
                      key={i}
                      className="h-8 w-full rounded-md bg-gray-100 animate-pulse"
                    />
                  ))}
                </div>
              ),
            )}
          </div>
          {/* Bottom bar */}
          <div className="border-t border-gray-200 p-3 space-y-2">
            <div className="h-8 w-full rounded-md bg-gray-100 animate-pulse" />
            <div className="h-8 w-full rounded-md bg-gray-100 animate-pulse" />
          </div>
        </div>
      </div>

      {/* ── Main content area ───────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center px-3 py-2 shadow bg-white">
          <div className="w-7 h-7 rounded-md bg-gray-200 animate-pulse mr-2" />
          <div className="w-16 h-7 rounded bg-gray-200 animate-pulse" />
        </div>

        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="py-8">
            <div className="max-w-[90%] 2xl:max-w-7xl md:px-8 w-full mx-auto">
              {/* Page title skeleton */}
              <div className="mb-8">
                <div className="w-60 h-10 rounded bg-gray-200 animate-pulse" />
              </div>

              {/* Two-column content — matches step 2's flex layout */}
              <div className="flex flex-row space-x-8 pb-20">
                {/* ── Left: skeleton form sections ──────────────────────── */}
                <div className="pt-6 max-w-full flex-1 space-y-4">
                  <SkeletonSection
                    title="Personal information"
                    phaseIndex={0}
                    currentPhase={phase}
                  >
                    <div className="flex gap-3 mb-4">
                      <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                      <div className="flex-1 grid grid-cols-3 gap-3">
                        {[1, 2, 3].map(i => (
                          <div key={i} className="space-y-1.5">
                            <SkeletonLine w="w-16" h="h-3" />
                            <SkeletonLine h="h-9" />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="space-y-1.5">
                          <SkeletonLine w="w-20" h="h-3" />
                          <SkeletonLine h="h-9" />
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 space-y-1.5">
                      <SkeletonLine w="w-24" h="h-3" />
                      <SkeletonLine h="h-16" />
                    </div>
                  </SkeletonSection>

                  <SkeletonSection
                    title="Social Media Links"
                    phaseIndex={1}
                    currentPhase={phase}
                  >
                    <div className="space-y-3">
                      {[1, 2].map(i => (
                        <div key={i} className="flex items-center gap-3">
                          <SkeletonLine w="w-32" h="h-9" />
                          <SkeletonLine h="h-9" />
                        </div>
                      ))}
                    </div>
                  </SkeletonSection>

                  <SkeletonSection
                    title="Company Information"
                    phaseIndex={1}
                    currentPhase={phase}
                  >
                    <div className="grid grid-cols-2 gap-3">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="space-y-1.5">
                          <SkeletonLine w="w-28" h="h-3" />
                          <SkeletonLine h="h-9" />
                        </div>
                      ))}
                    </div>
                  </SkeletonSection>

                  <SkeletonSection
                    title="Profile settings"
                    phaseIndex={2}
                    currentPhase={phase}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <SkeletonLine w="w-10" h="h-6" />
                        <SkeletonLine w="w-48" h="h-3" />
                      </div>
                      <div className="flex items-center gap-4">
                        <SkeletonLine w="w-10" h="h-6" />
                        <SkeletonLine w="w-56" h="h-3" />
                      </div>
                    </div>
                  </SkeletonSection>
                </div>

                {/* ── Right: Lottie inside the exact profile preview card ── */}
                <div
                  className="hidden xl:block w-full max-w-[352px] md:max-w-[388px] h-fit sticky top-0 pt-8 flex-shrink-0"
                  style={{ zoom: 0.8 }}
                >
                  <div className="p-4 rounded-md bg-white ring-1 ring-gray-200 shadow-lg overflow-hidden">
                    {/* h-[695px] matches ProfilePreview's max-h-[695px] exactly */}
                    <div
                      ref={containerRef}
                      className="w-full h-[695px] overflow-hidden"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ── Footer skeleton — matches InfoPanelFooter offset ───────────── */}
      <div className="fixed bottom-0 left-0 w-full lg:w-[calc(100%-235px)] lg:left-[235px] bg-white border-t border-l border-gray-300 px-4 py-3.5 z-10">
        <div className="flex justify-end space-x-3">
          <div className="w-20 h-9 rounded-lg bg-gray-200 animate-pulse" />
          <div className="w-24 h-9 rounded-lg bg-gray-300 animate-pulse" />
        </div>
      </div>

      {/* ── Company selection modal (shown once animation reaches company phase) ── */}
      {showModal && pendingModalData && (
        <CompanySelectionModal
          isOpen={showModal}
          positions={pendingModalData.positions}
          onSelect={handleCompanySelect}
          onClose={handleModalClose}
          loading={companyLookupLoading}
        />
      )}
    </div>
  );
}

export default LottieTransition;
